/**
 * useCacheStats - Hook per recuperare statistiche cache offline
 * Recupera conteggi dai metadata salvati durante il preload
 */
import { useState, useEffect, useCallback } from 'react';
import { useOfflineStorageV2 } from '@/core/offline/useOfflineStorageV2';
import { logger } from '@/shared/utils/logger';

export interface CacheStats {
  raProfiles: number;
  shopProducts: number;
  shopDeliveries: number;
  laundryMovements: number;
  lastPreloadTime: Date | null;
  isLoading: boolean;
}

const INITIAL_STATS: CacheStats = {
  raProfiles: 0,
  shopProducts: 0,
  shopDeliveries: 0,
  laundryMovements: 0,
  lastPreloadTime: null,
  isLoading: true,
};

export const useCacheStats = () => {
  const [stats, setStats] = useState<CacheStats>(INITIAL_STATS);
  const { isInitialized, getPreloadMetadata } = useOfflineStorageV2();

  const refreshStats = useCallback(async () => {
    if (!isInitialized) {
      setStats(prev => ({ ...prev, isLoading: false }));
      return;
    }

    setStats(prev => ({ ...prev, isLoading: true }));

    try {
      const metadata = await getPreloadMetadata();

      if (metadata) {
        setStats({
          raProfiles: metadata.raProfilesCount || 0,
          shopProducts: metadata.shopProductsCount || 0,
          shopDeliveries: metadata.shopDeliveriesCount || 0,
          laundryMovements: metadata.laundryMovementsCount || 0,
          lastPreloadTime: metadata.lastPreloadTime
            ? new Date(metadata.lastPreloadTime)
            : null,
          isLoading: false,
        });
      } else {
        // Nessun metadata = cache vuota
        setStats({ ...INITIAL_STATS, isLoading: false });
      }
    } catch (error) {
      logger.error('[useCacheStats] Error loading stats:', error);
      setStats(prev => ({ ...prev, isLoading: false }));
    }
  }, [isInitialized, getPreloadMetadata]);

  // Carica stats all'init e quando db cambia
  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  return { ...stats, refreshStats };
};

export default useCacheStats;
